/**
 * Persists the Configure unified-list filters (search, labels, status,
 * sort) so leaving the page and coming back keeps the same view.
 * Best-effort localStorage — a missing or corrupted blob returns
 * defaults rather than throwing.
 */

export type ConfigureSortKey = 'name' | 'updated' | 'last_fired'

export interface ConfigureListPrefs {
  search: string
  labels: string[]
  statuses: string[]
  sortBy: ConfigureSortKey
}

export const LIST_PREFS_STORAGE_KEY = 'ontology.configure.listPrefs'

export const DEFAULT_LIST_PREFS: ConfigureListPrefs = {
  search: '',
  labels: [],
  statuses: [],
  sortBy: 'name'
}

const SORT_KEYS = new Set<ConfigureSortKey>(['name', 'updated', 'last_fired'])
const STATUS_KEYS = new Set(['active', 'paused'])

function hasLocalStorage(): boolean {
  return typeof window !== 'undefined' && 'localStorage' in window
}

function asStringArray(value: unknown, allowed?: Set<string>): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  for (const item of value) {
    if (typeof item !== 'string' || item === '') continue
    if (allowed !== undefined && !allowed.has(item)) continue
    out.push(item)
  }
  return out
}

export function readListPrefs(): ConfigureListPrefs {
  if (!hasLocalStorage()) return { ...DEFAULT_LIST_PREFS }
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(LIST_PREFS_STORAGE_KEY)
  } catch {
    return { ...DEFAULT_LIST_PREFS }
  }
  if (raw === null) return { ...DEFAULT_LIST_PREFS }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_LIST_PREFS }
    const rec = parsed as {
      search?: unknown
      labels?: unknown
      statuses?: unknown
      sortBy?: unknown
    }
    const sortBy =
      typeof rec.sortBy === 'string' && SORT_KEYS.has(rec.sortBy as ConfigureSortKey)
        ? (rec.sortBy as ConfigureSortKey)
        : DEFAULT_LIST_PREFS.sortBy
    return {
      search: typeof rec.search === 'string' ? rec.search : '',
      labels: asStringArray(rec.labels),
      statuses: asStringArray(rec.statuses, STATUS_KEYS),
      sortBy
    }
  } catch {
    return { ...DEFAULT_LIST_PREFS }
  }
}

export function writeListPrefs(prefs: ConfigureListPrefs): void {
  if (!hasLocalStorage()) return
  try {
    window.localStorage.setItem(LIST_PREFS_STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    // best-effort; persistence is a convenience, not a correctness requirement
  }
}
